import {ReactElement, useContext, useMemo, useState} from "react";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {RevenueModelType, useSetPropertyRevenueModelMutation} from "../../../__generated__/generated_types";
import {toast} from "react-toastify";
import {getRevenueModelsForUser} from "./revenue-modal-options";
import {AuthContext} from "../../../contexts/AuthContext";

export interface RevenueModelModalProps {
    onClose: () => void;
    onUpdate: () => void;
    budgetYear: number;
    properties: {id: string, systemName: string, name: string | null | undefined}[];
}

// This component is used by both normal properties
// and acquisition properties.
// ManualPropertyList.tsx and AcquisitionProperties.tsx
export function RevenueModelModal(props: RevenueModelModalProps): ReactElement {
    const {user} = useContext(AuthContext);

    const [selectedModel, setSelectedModel] = useState<string>("");
    const [selectModelTouched, setSelectModelTouched] = useState<boolean>(false);

    const [setRevenueModel, {loading: setRevenueModelLoading}] = useSetPropertyRevenueModelMutation({
        notifyOnNetworkStatusChange: true,
    });

    const revenueModelOptions = useMemo(() => getRevenueModelsForUser(user), [user]);

    function doSetRevenueModel() {
        const modelOptions = revenueModelOptions.map(o => o.value);
        if(modelOptions.includes(selectedModel as RevenueModelType)) {
            setRevenueModel({
                variables: {
                    propertyIds: props.properties.map(p => p.id),
                    budgetYear: props.budgetYear,
                    revenueModelType: selectedModel as RevenueModelType
                }
            }).then(res => {
                if(res.data?.setPropertyRevenueType) {
                    toast.success(`Revenue Model Updated for ${props.properties.length} Properties.`);
                    props.onUpdate();
                } else {
                    toast.error("Error Updated Revenue Model.");
                }
            }).catch(() => {
                toast.error("Error Updated Revenue Model.");
            });
        }
    }

    const names = props.properties.limit(5).map(p => p.name ?? p.systemName);
    const moreCount = props.properties.length > 5 ? props.properties.length - 5 : 0;
    return <Modal onClose={props.onClose}>
        <Header>Set Revenue Model</Header>
        <Body>
            <div>Set Revenue Model for {names.join(", ")} {moreCount > 0 ? `+${moreCount}` : ""}</div>
            <form>
                <CustomZDDropdown
                    applySelectedItems={items => {
                        if(Array.isArray(items) && items.length > 0) {
                            setSelectedModel(items[0]);
                            setSelectModelTouched(true);
                        } else if(typeof items === "string") {
                            setSelectedModel(items);
                            setSelectModelTouched(true);
                        }
                    }}
                    openDropdownPlaceholder={"Revenue Model"}
                    closedDropdownPlaceholder={undefined}
                    options={revenueModelOptions}
                    isError={selectModelTouched && !revenueModelOptions.find(o => o.value === selectedModel)}
                    label={"Revenue Model"}
                />
            </form>
        </Body>
        <Footer>
            <FooterItem>
                <Button isBasic isDanger onClick={props.onClose}>Cancel</Button>
            </FooterItem>
            <FooterItem>
                <Button isPrimary onClick={doSetRevenueModel} disabled={setRevenueModelLoading || !revenueModelOptions.find(o => o.value === selectedModel)}>Set</Button>
            </FooterItem>
        </Footer>
        <Close aria-label="Close set revenue model modal"/>
    </Modal>;
}